import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { FormImageProps } from 'src/features/generalForms/builder/types/Image';
import { useContextSelector } from 'use-context-selector';
import { NodeId, useEditor, useNode } from '@craftjs/core';
import { AttachmentsContext, LayoutAttachmentType } from './attachments.context';
import { getFileById } from 'src/services/api/api-client/FilesClient';
import { useTranslation } from 'react-i18next';
import { FilesQuery } from 'src/services/api/api-client';
import { downloadFile } from 'src/helpers/file-helper';

export const AttachmentsContextProvider: FC<
  PropsWithChildren<{
    initialAttachments?: Record<NodeId, LayoutAttachmentType>;
    uploadFileFunction?: (localFile: LayoutAttachmentType) => Promise<string>;
  }>
> = ({ children, uploadFileFunction, initialAttachments }) => {
  const {
    actions: { setProp },
  } = useEditor();

  const [attachments, setAttachments] = useState<Record<NodeId, LayoutAttachmentType>>(initialAttachments ?? {});
  const addAttachment = useCallback(
    (nodeId: NodeId, attachment: LayoutAttachmentType) => setAttachments((prev) => ({ ...prev, [nodeId]: attachment })),
    [],
  );

  const uploadFiles = useCallback(async () => {
    if (!uploadFileFunction) throw Error('uploadFileFunction should be provided');
    const uploadFilePromises = Object.entries(attachments)
      // NOTE: upload only local files (without file id)
      .filter(([, value]) => !value.fileId)
      .map(
        ([nodeId, file]) =>
          new Promise<void>(async (resolve, reject) => {
            try {
              const fileId = await uploadFileFunction(file);
              setProp(nodeId, (props: FormImageProps) => (props.attachment!.fileId = fileId));
              resolve();
            } catch (error) {
              reject(error);
            }
          }),
      );

    await Promise.all(uploadFilePromises);
  }, [attachments, setProp, uploadFileFunction]);

  const removeAttachment = useCallback(
    (nodeId: NodeId) =>
      setAttachments((prev) => {
        delete prev[nodeId];
        return prev;
      }),
    [],
  );

  const downloadFilesHandler = useCallback(async () => {
    const fileIds = Object.values(attachments)
      .flatMap((a) => a.fileId)
      .filter(Boolean) as string[];

    const images = await FilesQuery.Client.getFiles(fileIds);
    downloadFile(images);
  }, [attachments]);

  return (
    <AttachmentsContext.Provider
      value={{
        attachments,
        addAttachment,
        removeAttachment,
        uploadFiles: uploadFileFunction ? uploadFiles : undefined,
        downloadFiles: downloadFilesHandler,
      }}
    >
      {children}
    </AttachmentsContext.Provider>
  );
};

export const useFieldAttachments = (options?: { preventDownloading?: boolean }) => {
  const {
    id: nodeId,
    actions: { setProp },
    props,
  } = useNode((x) => ({
    props: x.data.props as FormImageProps,
  }));
  const { t } = useTranslation();
  const { attachments, addAttachment, removeAttachment } = useContextSelector(AttachmentsContext, (x) => x);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);

  const downloadAttachment = useCallback(async () => {
    const fileId = props.attachment?.fileId;
    if (!fileId || attachments[nodeId]?.file) return;

    try {
      setLoading(true);
      const result = await getFileById(fileId);
      addAttachment(nodeId, {
        // @ts-ignore Is used Blob instead File
        file: result.data,
        fileId: fileId,
        fileName: result.fileName!,
        nodeId: nodeId,
      });
    } catch (error) {
      setErrorMessage(t('LocalErrors.FileNotFound', { fileId: fileId }));
    } finally {
      setLoading(false);
    }
  }, [addAttachment, attachments, nodeId, props.attachment?.fileId, t]);

  // This effect downloads image on mount
  useEffect(() => {
    if (options?.preventDownloading) return;
    downloadAttachment();
  }, [downloadAttachment, options?.preventDownloading]);

  const changeAttachmentOnLayout = useCallback(
    (attachment: LayoutAttachmentType | null) => {
      setProp((p: FormImageProps) => {
        p.attachment = !attachment
          ? undefined
          : {
              fileId: null,
              fileName: attachment.fileName,
            };
      });
    },
    [setProp],
  );

  const handleAttachmentChange = useCallback(
    (appAttachment: LayoutAttachmentType | null) => {
      setErrorMessage(undefined);
      if (appAttachment) addAttachment(nodeId, appAttachment);
      else removeAttachment(nodeId);

      changeAttachmentOnLayout(appAttachment);
    },
    [addAttachment, nodeId, removeAttachment, changeAttachmentOnLayout],
  );

  const imageUrl = useMemo(() => {
    const attachment = attachments[nodeId];
    if (!attachment?.file) return;
    return URL.createObjectURL(attachment.file);
  }, [attachments, nodeId]);

  return {
    attachment: attachments[nodeId],
    errorMessage: errorMessage,
    loading: loading,
    imageUrl,
    onChange: handleAttachmentChange,
  };
};
